import React, { useContext, useState } from 'react'
import styled, { css } from 'styled-components'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import axios from 'axios'
import media from 'css-in-js-media'
import Button, { StyledButton } from './Button'
import { Variables } from '../styles/Variables'
import Checkbox, { StyledCheckbox } from './Checkbox'
import MobileExtension from './MobileExtension'
import { Select, StyledSelect } from './FormElements'
import LayoutContext from './LayoutContext'
import { Input, Textarea, InputRow, ValidationError } from './FormElements'

const FormMessages = styled.div`
    padding-top: 20px;
`

const SuccessFormMessages = styled.div`
    font-size: 16px;
    line-height: 175%;
    letter-spacing: 0.005em;
    color: ${Variables.color.white};
    text-align: center;

    ${media('>=desktop')} {
        margin-top: 60px;
    }
`

const DarkThemeInput = css`
    color: ${Variables.color.white};

    :focus {
        border-color: #968d86;
    }

    ::placeholder {
        color: ${Variables.color.white};
    }

    :-webkit-autofill {
        -webkit-box-shadow: 0 0 0 50px ${Variables.color.cocoa} inset;
        -webkit-text-fill-color: ${Variables.color.white};
    }
`

const DarkThemeStyling = css`
    color: ${Variables.color.cocoa};

    ${StyledSelect}, ${Input}, ${Textarea} {
        ${DarkThemeInput}
    }

    ${StyledSelect} {
        /* background-image: url("data:image/svg+xml,%3Csvg width='14' height='9' viewBox='0 0 14 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.25 1.5L7 7.75L0.75 1.5' stroke='white' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");        */
    }

    ${StyledCheckbox} {
        border-color: ${Variables.color.white};
        border-width: 1px;
    }

    a {
        color: ${Variables.color.white};
    }
`

export const GDPR_Notice = styled.div`
    padding-top: 20px;
    letter-spacing: 0.0025em;
    width: 100%;
    font-family: ${Variables.font.body};
    font-weight: 300;
    font-size: 20px;
    line-height: 120%;
    color: ${Variables.color.white};

    ${media('<desktop')} {
        font-size: 16px;
    }

    a {
        color: ${Variables.color.white};
        font-weight: 500;
        border-bottom: 1px solid ${Variables.color.white};
    }
`

const AlternativeLayout = css`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    ${InputRow} {
        ${media('>=desktop')} {
            max-width: 49%;
        }

        :first-child {
            margin-top: auto;
        }

        &.fluid {
            max-width: 100%;
        }
    }
`

const LightThemeStyling = css`
    ${InputRow} {
        margin: 6px 0;
    }

    .is-textarea {
        margin: 10px 0 22px;
    }
`

const StyledInputRow = styled(InputRow)`
    ${media('<desktop')} {
        display: none;
    }
`

const TheForm = styled(Form)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    a {
        color: white;
        font-weight: 500;
        transition: 0.1s border-color ease-out;

        :hover {
            border-color: white;
        }
    }

    ${(props) => (props.darktheme ? DarkThemeStyling : LightThemeStyling)}
    ${(props) => props.layout == '2' && AlternativeLayout}

    ${StyledButton} {
        height: 44px;
        margin-top: 40px;
        ${media('<desktop')} {
            height: 40px;
            font-size: 12px;
            width: 209px;
            margin: 0 auto;
            margin-top: 30px;
        }

        ${media('>desktop')} {
            max-width: 228px;
            min-width: 228px;
        }
    }
`

const CheckBoxInputRow = styled(InputRow)`
    margin-top: 36px;
    ${media('<desktop')} {
        margin-top: 10px;
    }
`

const PhoneLabel = styled.span`
    color: ${Variables.color.white};
    border-bottom: 1px solid ${Variables.color.white};
    align-items: center;
    display: flex;
    font-family: ${Variables.font.body};
    font-size: 20px;
    width: 100%;
    ${media('<desktop')} {
        font-size: 16px;
    }
`

const ButtonWrap = styled.div`
    color: ${Variables.color.white};
    width: 100%;
    text-align: center;
`

const ContactForm: React.FC<{
    darktheme?: boolean
    layout?: string
}> = ({ darktheme, layout }) => {
    const { currentLang } = useContext(LayoutContext)
    const [isSubmitting, setSubmitting] = useState(false)
    const [formMessage, setFormMessage] = useState('')

    const intl = useIntl()

    const form_schema = yup.object().shape({
        Salutation: yup.string().required(
            intl.formatMessage({
                id: 'Title is required',
            })
        ),
        FirstName: yup
            .string()
            .min(
                2,
                intl.formatMessage({
                    id: 'First name must be at least 2 characters',
                })
            )
            .required(
                intl.formatMessage({
                    id: 'First name is required',
                })
            ),
        LastName: yup
            .string()
            .min(
                2,
                intl.formatMessage({
                    id: 'Last name must be at least 2 characters',
                })
            )
            .required(
                intl.formatMessage({
                    id: 'Last name is required',
                })
            ),
        Email: yup
            .string()
            .email(
                intl.formatMessage({
                    id: 'Invalid email',
                })
            )
            .required(
                intl.formatMessage({
                    id: 'Email is required',
                })
            ),
        pba__Comments__c: yup.string().required(
            intl.formatMessage({
                id: 'Message is required',
            })
        ),
    })

    const formSubmittion = (values, actions) => {
        setSubmitting(true)
        setFormMessage('')

        const updtesMsg = values.asa_NewsletterWebsiteForm__c
            ? '\n– I want to receive updates'
            : ''

        let formdata_Schema = {
            prospect: {
                token: '2773fdfab6c671833c8488336664da34',
                contact: {
                    Preferred_language__c: currentLang
                        ? currentLang.labelFull
                        : 'Unknown',
                    ContactType__c: 'Lead',
                    LeadSource: 'Internal (ASA)',
                    asa_ChannelOfTheEnquiry__c: 'Website Pazola',
                    Salutation: values.Salutation,
                    asa_Gender__c:
                        values.Salutation == 'Mr.' ||
                        values.Salutation == 'Herr'
                            ? 'Male'
                            : 'Female',
                    FirstName: values.FirstName,
                    LastName: values.LastName,
                    Phone: `+${values.CountryCode} ${values.Phone}`,
                    asa_Country__c: 'Unknown',
                    Email: values.Email,
                    asa_GdprWebsiteForm__c: 1,
                    asa_NewsletterWebsiteForm__c: values.asa_NewsletterWebsiteForm__c
                        ? 'yes'
                        : 'no',
                },
                request: {
                    asa_ChannelOfTheEnquiry__c: 'Website Pazola',
                    Motivation_der_Anfrage__c: 'Unknown',
                    asa_Masterproperty__c: 'Pazola',
                    Typ_der_Immobilie__c: 'Apartment',
                    pba__Status__c: 'Enquiry',
                    pba__Comments__c: `${values.pba__Comments__c}${updtesMsg}`,
                },
            },
        }

        var formConfig = {
            method: 'post',
            url:
                'https://andermatt-swissalps.my.salesforce-sites.com/services/apexrest/pba/webtoprospect/v1/',
            headers: {
                'Content-Type': 'application/json',
            },
            data: JSON.stringify(formdata_Schema),
        }

        axios(formConfig)
            .then(function (response) {
                setSubmitting(false)
                setFormMessage(
                    intl.formatMessage({
                        id: 'Thank you for getting in touch.',
                    })
                )
            })
            .catch(function (error) {
                setSubmitting(false)
                // console.log(error)
                setFormMessage(
                    intl.formatMessage({
                        id: 'There was an issue submitting the form.',
                    })
                )
            })
    }

    return (
        <Formik
            initialValues={{
                Salutation: '',
                FirstName: '',
                LastName: '',
                CountryCode: '41',
                Email: '',
                Phone: '',
                pba__Comments__c: '',
                asa_NewsletterWebsiteForm__c: false,
            }}
            validationSchema={form_schema}
            onSubmit={(values, actions) => {
                formSubmittion(values, actions)
            }}
        >
            {({ values, errors }) => {
                return (
                    <>
                        {formMessage ==
                            intl.formatMessage({
                                id: 'Thank you for getting in touch.',
                            }) && (
                            <SuccessFormMessages>
                                {intl.formatMessage({
                                    id: 'Thank you for getting in touch.',
                                })}
                            </SuccessFormMessages>
                        )}
                        {formMessage !=
                            intl.formatMessage({
                                id: 'Thank you for getting in touch.',
                            }) && (
                            <TheForm
                                name="ContactForm"
                                darktheme={darktheme}
                                layout={layout}
                            >
                                <InputRow
                                    error={errors.Salutation ? true : false}
                                >
                                    <Select
                                        component="select"
                                        name="Salutation"
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                    >
                                        <option disabled value="" hidden>
                                            {intl.formatMessage({
                                                id: 'Title',
                                            })}
                                        </option>
                                        <option value="Mr.">
                                            {intl.formatMessage({
                                                id: 'Mr',
                                            })}
                                        </option>
                                        <option value="Mrs.">
                                            {intl.formatMessage({
                                                id: 'Mrs',
                                            })}
                                        </option>
                                    </Select>
                                    <ValidationError
                                        name="Salutation"
                                        component="div"
                                    />
                                </InputRow>

                                <StyledInputRow error={false}></StyledInputRow>

                                <InputRow
                                    error={errors.FirstName ? true : false}
                                >
                                    <Input
                                        type="text"
                                        placeholder={intl.formatMessage({
                                            id: 'Firstname',
                                        })}
                                        name="FirstName"
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                    />
                                    <ValidationError
                                        name="FirstName"
                                        component="div"
                                    />
                                </InputRow>

                                <InputRow
                                    error={errors.LastName ? true : false}
                                >
                                    <Input
                                        type="text"
                                        placeholder={intl.formatMessage({
                                            id: 'Lastname',
                                        })}
                                        name="LastName"
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                    />
                                    <ValidationError
                                        name="LastName"
                                        component="div"
                                    />
                                </InputRow>

                                <InputRow
                                    group="true"
                                    error={errors.Phone ? true : false}
                                >
                                    <PhoneLabel>Phone</PhoneLabel>
                                    <MobileExtension
                                        isSubmitting={isSubmitting}
                                    />
                                    <Input
                                        type="text"
                                        placeholder="000 000 000"
                                        name="Phone"
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                    />
                                    <ValidationError
                                        name="Phone"
                                        component="div"
                                    />
                                </InputRow>

                                <InputRow error={errors.Email ? true : false}>
                                    <Input
                                        type="email"
                                        placeholder={intl.formatMessage({
                                            id: 'Email',
                                        })}
                                        name="Email"
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                    />
                                    <ValidationError
                                        name="Email"
                                        component="div"
                                    />
                                </InputRow>

                                <InputRow
                                    className="is-textarea"
                                    error={
                                        errors.pba__Comments__c ? true : false
                                    }
                                >
                                    <Textarea
                                        component="textarea"
                                        name="pba__Comments__c"
                                        placeholder={intl.formatMessage({
                                            id: 'Message',
                                        })}
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                    />
                                    <ValidationError
                                        name="pba__Comments__c"
                                        component="div"
                                    />
                                </InputRow>

                                <CheckBoxInputRow>
                                    <Checkbox
                                        name="asa_NewsletterWebsiteForm__c"
                                        label={intl.formatMessage({
                                            id: 'Checkbox_updates',
                                        })}
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                        darktheme={darktheme}
                                    />
                                    <GDPR_Notice
                                        dangerouslySetInnerHTML={{
                                            __html: intl.formatMessage({
                                                id: 'Form_GDPR',
                                            }),
                                        }}
                                    ></GDPR_Notice>
                                </CheckBoxInputRow>

                                <ButtonWrap>
                                    <Button
                                        type="submit"
                                        secondary
                                        full
                                        textalign="center"
                                        disabled={
                                            isSubmitting ? 'disabled' : false
                                        }
                                    >
                                        <FormattedMessage
                                            id={
                                                isSubmitting
                                                    ? 'Submiting'
                                                    : 'Submit'
                                            }
                                        />
                                    </Button>
                                    {formMessage ==
                                        intl.formatMessage({
                                            id:
                                                'There was an issue submitting the form.',
                                        }) && (
                                        <FormMessages>
                                            {formMessage}
                                        </FormMessages>
                                    )}
                                </ButtonWrap>
                            </TheForm>
                        )}
                    </>
                )
            }}
        </Formik>
    )
}

export default ContactForm
